import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { EmployeeAvatar } from '@modernloop/shared/components';
import { AvatarGroup, Stack, SxProps, Theme } from '@mui/material';

import { EmployeeFacePile_EmployeeFragment } from 'src/generated/mloop-graphql';

import { FCWithFragments, createSxProps } from 'src/components/types';

type AvatarSize = 16 | 20 | 28 | 32 | 40 | 80 | 120 | 210;

const useSxProps = () => {
  const sxProps = useMemo(
    () => ({
      avatarWrapper: {
        alignItems: 'flex-start',
      },
      avatarItems: {
        '&>div': {
          width: '24px',
          height: '24px',
          fontSize: '12px',
        },
      },
    }),
    []
  );

  return createSxProps(sxProps);
};

interface EmployeeFacePileProps {
  max?: number;
  avatarSize?: AvatarSize;
  sx?: SxProps<Theme>;
  dataTestId?: string;
}

type Fragments = {
  employees: EmployeeFacePile_EmployeeFragment[];
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const EmployeeFacePile: FCWithFragments<Fragments, EmployeeFacePileProps> = ({
  sx,
  employees,
  dataTestId,
  max = 3,
  avatarSize = 20,
}) => {
  const sxProps = useSxProps();
  return (
    <Stack sx={{ ...sxProps.avatarWrapper, ...sx }}>
      <AvatarGroup max={max} sx={sxProps.avatarItems}>
        {employees.map((employee) => (
          <EmployeeAvatar
            key={employee.id}
            data-testid={`${dataTestId}-${employee.id}`}
            imageUrl={employee?.internalProfilePictureUrl}
            fullName={employee?.fullName}
            size={avatarSize}
          />
        ))}
      </AvatarGroup>
    </Stack>
  );
};

EmployeeFacePile.fragments = {
  employees: gql`
    fragment EmployeeFacePile_employee on Employee {
      id
      fullName
      internalProfilePictureUrl
    }
  `,
};

export default EmployeeFacePile;
